import React from 'react';
import { ToolResultProps } from '../types';
import { ThoughtMarkdown } from '../ThoughtMarkdown';
import { Card } from '@/components/ui/card';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { ExternalLink, ImageIcon, Clipboard, Loader2, Check, X } from 'lucide-react';

interface WebSearchImage {
  url: string;
  description?: string;
}

interface WebSearchResultItem {
  url: string;
  title: string;
  content: string;
  score?: number;
  favicon?: string;
}

interface WebSearchContent {
  query: string;
  follow_up_questions?: string[] | null;
  answer: string;
  images?: WebSearchImage[] | null;
  results?: WebSearchResultItem[] | null;
}

interface ImageWithCopyProps {
  url: string;
  description?: string;
}

const ImageWithCopy: React.FC<ImageWithCopyProps> = ({ url, description }) => {
  const [copyStatus, setCopyStatus] = React.useState<'idle' | 'copying' | 'success' | 'error'>('idle');

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setCopyStatus('copying');
    
    try {
      // Word Optimized Strategy: Copy HTML
      // We write an <img> tag to the clipboard. Word will interpret this and download the image.
      const htmlContent = `<img src="${url}" alt="${description || 'Image'}" />`;
      
      const blobHtml = new Blob([htmlContent], { type: 'text/html' });
      const blobText = new Blob([url], { type: 'text/plain' });
      
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': blobHtml,
          'text/plain': blobText
        })
      ]);
      
      setCopyStatus('success');
      setTimeout(() => setCopyStatus('idle'), 2000);
    } catch (err) {
      console.error("Copy failed", err);
      setCopyStatus('error');
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  return (
    <div className="shrink-0 overflow-hidden rounded-md cursor-pointer group relative" style={{ width: '200px', height: '120px' }}>
      <img 
        src={url} 
        alt={description || `Search image`} 
        className="aspect-video h-full w-full object-cover transition-transform group-hover:scale-105"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
          // Show fallback icon
          ((e.target as HTMLImageElement).nextSibling as HTMLElement).style.display = 'flex';
        }}
      />
      {/* Fallback for broken images */}
      <div className="hidden absolute inset-0 bg-gray-200 items-center justify-center">
         <ImageIcon className="h-8 w-8 text-gray-400" />
      </div>

      {/* Copy Button - Always Visible */}
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors z-10"
        title="Copiar imagen para Word"
      >
        {copyStatus === 'idle' && <Clipboard className="w-3 h-3" />}
        {copyStatus === 'copying' && <Loader2 className="w-3 h-3 animate-spin" />}
        {copyStatus === 'success' && <Check className="w-3 h-3 text-green-400" />}
        {copyStatus === 'error' && <X className="w-3 h-3 text-red-400" />}
      </button>

      {/* Caption overlay on hover */}
      {description && (
         <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 opacity-0 transition-opacity group-hover:opacity-100">
           <p className="text-[10px] text-white truncate">{description}</p>
         </div>
      )}
    </div>
  );
};

const WebSearchResult: React.FC<ToolResultProps> = ({ thought, onDocumentClick }) => {
  const { content, success, toolResult } = thought;
  
  // Parse content if it's a JSON string
  let searchData: WebSearchContent | null = null;
  let rawContent = content;

  // Try to parse the content
  try {
    // 0. Check if metadata has json_results (User's new backend format)
    if (toolResult?.metadata?.json_results) {
        // It might be a string (if backend sent JSON string) or object (if already parsed)
        const jsonResults = typeof toolResult.metadata.json_results === 'string' 
           ? JSON.parse(toolResult.metadata.json_results)
           : toolResult.metadata.json_results;
        
        if (jsonResults) {
            searchData = {
                query: jsonResults.query,
                answer: jsonResults.answer,
                images: jsonResults.images,
                results: jsonResults.results,
                follow_up_questions: jsonResults.follow_up_questions
            };
        }
    }

    // 1. Check if it's already a JSON object (if thought.content was pre-parsed)
    if (!searchData && typeof content === 'object' && content !== null) {
      searchData = content as WebSearchContent;
    }
    // 2. Try to parse as JSON string
    else if (!searchData && typeof content === 'string') {
      const trimmed = content.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
           searchData = JSON.parse(trimmed);
        } catch (e) {
           console.log('JSON parse failed, trying markdown parser');
        }
      }
      
      // 3. Fallback: Parse the specific Markdown format from the backend
      if (!searchData) {
        searchData = parseMarkdownSearch(content);
      }
    }
  } catch (e) {
    console.error("Failed to parse web search result", e);
  }

  // If we couldn't parse structured data, show the default view (text)
  if (!searchData || (!searchData.answer && !searchData.images && !searchData.results)) {
    return (
      <div className="mt-2 text-sm text-gray-800">
         <ThoughtMarkdown 
            content={rawContent || ''} 
            onViewDocument={onDocumentClick ? (url, name) => onDocumentClick('', url, name) : undefined}
         />
         {/* DEBUG INFO */}
         <div className="mt-2 p-2 bg-red-50 text-[10px] text-red-800 hidden">
            Failed to parse content. content length: {content?.length}
         </div>
      </div>
    );
  }
  
  const [hoveredUrl, setHoveredUrl] = React.useState<string | null>(null);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (url: string) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredUrl(url);
    }, 600); // 600ms delay to prevent accidental triggers
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredUrl(null);
    }, 300);
  };

  const { answer, images, results } = searchData;
  const hasImages = images && images.length > 0;
  const hasResults = results && results.length > 0;

  return (
    <div className="mt-2 flex flex-col gap-4 w-full relative">
      
      {/* Search Preview Overlay */}
      {hoveredUrl && (
        <div 
          className="fixed z-50 rounded-lg shadow-2xl border bg-white overflow-hidden flex flex-col animation-in fade-in zoom-in-95 duration-200"
          style={{
            bottom: '20px',
            right: '20px',
            width: '400px',
            height: '500px',
            pointerEvents: 'auto' 
          }}
          onMouseEnter={() => {
            if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
          }}
          onMouseLeave={handleMouseLeave}
        >
           {/* Header with actions */}
           <div className="bg-gray-100 p-2 flex justify-between items-center border-b shrink-0">
              <div className="text-xs font-semibold text-gray-600 truncate flex-1 mr-2">{hoveredUrl}</div>
              <button 
                  onClick={(e) => {
                      e.stopPropagation();
                      window.open(hoveredUrl, '_blank');
                  }}
                  className="text-xs bg-white hover:bg-gray-200 text-gray-700 px-2 py-1 rounded border transition-colors flex items-center gap-1"
              >
                  <ExternalLink className="w-3 h-3" /> Abrir
              </button>
           </div>
           
           {/* Iframe Preview */}
           <div className="flex-1 bg-white relative w-full h-full">
               <iframe 
                  src={hoveredUrl} 
                  className="w-full h-full border-0"
                  title="Preview"
                  sandbox="allow-scripts allow-same-origin"
                  loading="lazy"
                  onError={(e) => console.error("Iframe error", e)}
               />
               
               {/* Fallback/Loading hint (rendered behind iframe or if iframe fails visual check is hard) */}
               <div className="absolute inset-0 flex items-center justify-center -z-10 text-gray-400 text-xs">
                  Cargando vista previa...
               </div>
           </div>
        </div>
      )}

      {/* 1. Images Carousel */}
      {hasImages && (
        <div className="w-full">
             <ScrollArea className="w-full whitespace-nowrap rounded-md border bg-muted/20">
                <div className="flex w-max space-x-4 p-4">
                  {images.map((img, idx) => (
                    <ImageWithCopy key={idx} url={img.url} description={img.description} />
                  ))}
                </div>
                <ScrollBar orientation="horizontal" />
            </ScrollArea>
        </div>
      )}

      {/* 2. Main Answer */}
      {answer && (
         <div className="text-sm text-gray-800 leading-relaxed">
             <ThoughtMarkdown 
                content={answer} 
                onViewDocument={onDocumentClick ? (url, name) => onDocumentClick('', url, name) : undefined}
             />
         </div>
      )}

      {/* 3. Source Cards */}
      {hasResults && (
        <div className="flex flex-col gap-2 mt-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Sources</h4>
          <div className="flex flex-row gap-2 overflow-x-auto pb-2 -mx-1 px-1">
            {results.map((result, idx) => (
              <a 
                key={idx} 
                href={result.url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="block group min-w-[250px] max-w-[250px]"
              >
                <Card className="flex flex-row items-start p-3 gap-3 h-full transition-colors hover:bg-muted/50 border-gray-200 shadow-sm hover:shadow-md relative">
                    {/* Favicon / Icon */}
                    <div className="shrink-0 mt-0.5">
                       {result.favicon ? (
                         <img src={result.favicon} alt="" className="w-4 h-4 object-contain" onError={(e) => (e.target as HTMLElement).style.display = 'none'} />
                       ) : (
                         <ExternalLink className="w-4 h-4 text-emerald-600" />
                       )}
                    </div>
                    
                    {/* Relevance Score Badge */}
                    {result.score !== undefined && (
                      <div className="absolute bottom-2 right-2 bg-emerald-100 text-emerald-800 text-[9px] font-medium px-1.5 py-0.5 rounded border border-emerald-200">
                        Relevancia: {(result.score * 100).toFixed(0)}%
                      </div>
                    )}
                    
                    <div className="flex flex-col gap-1 min-w-0 flex-1">
                      <div className="flex justify-between items-start gap-2">
                         <h5 className="text-xs font-medium text-blue-600 group-hover:underline truncate w-full" title={result.title}>
                           {result.title}
                         </h5>
                      </div>
                      <p className="text-[10px] text-muted-foreground leading-tight">
                        {result.content.length > 200 
                          ? result.content.substring(0, 200) + '...' 
                          : result.content}
                      </p>
                       <p className="text-[9px] text-gray-400 truncate mt-0.5">{new URL(result.url).hostname}</p>
                    </div>
                </Card>
              </a>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

// --- Helper Parser Functions ---

function parseMarkdownSearch(text: string): WebSearchContent {
  const result: WebSearchContent = {
    query: '',
    answer: '',
    images: [],
    results: []
  };

  if (!text) return result;

  // 1. Extract Images: ![description](url)
  const imageRegex = /!\[(.*?)\]\((.*?)\)/g;
  let imageMatch;
  while ((imageMatch = imageRegex.exec(text)) !== null) {
    if (result.images) {
        result.images.push({
            description: imageMatch[1],
            url: imageMatch[2]
        });
    }
  }

  // 2. Extract Direct Answer
  // It usually starts with "💡 **Respuesta directa:**" and ends before "🔍 **Resultados de búsqueda**"
  // Or it's just the text at the beginning if no headers found
  const directAnswerRegex = /💡 \*\*Respuesta directa:\*\*\s*([\s\S]*?)(?=🔍 \*\*Resultados de búsqueda\*\*|$)/;
  const answerMatch = text.match(directAnswerRegex);
  
  if (answerMatch && answerMatch[1]) {
    result.answer = answerMatch[1].trim();
  } else {
     // If no specific answer section, try to identify text before the results section
     const plainTextRegex = /([\s\S]*?)(?=🔍 \*\*Resultados de búsqueda\*\*|!\[)/;
     const plainMatch = text.match(plainTextRegex);
     if (plainMatch && plainMatch[1] && plainMatch[1].trim() !== '') {
        result.answer = plainMatch[1].trim();
     }
  }

  // 3. Extract Search Results (Sources)
  // Blocks starting with "**1. Title**"
  // We'll split the text by the results header first to avoid false positives in inputs
  const resultsSectionMatch = text.match(/🔍 \*\*Resultados de búsqueda\*\*[\s\S]*$/);
  const resultsSection = resultsSectionMatch ? resultsSectionMatch[0] : text;

  // Regex for individual results: **N. Title** ... content ...
  // This is tricky because the content format varies. We will iterate line by line or block by block.
  // A robust way for the provided format:
  
  // Find all blocks that look like a result item
  const itemRegex = /\*\*\d+\.\s+(.*?)\*\*([\s\S]*?)(?=\*\*\d+\.\s+|$)/g;
  let itemMatch;
  
  while ((itemMatch = itemRegex.exec(resultsSection)) !== null) {
      const title = itemMatch[1].trim();
      const body = itemMatch[2].trim();
      
      const urlMatch = body.match(/🔗 URL: (.*?)(?:\n|$)/);
      const url = urlMatch ? urlMatch[1].trim() : '';

      const snippetMatch = body.match(/📄 (.*?)(?:\n|$)/);
      // Fallback for snippet: sometimes it's just text lines without the emoji if format varies
      const snippet = snippetMatch ? snippetMatch[1].trim() : '';

      const relevanceMatch = body.match(/📊 Relevancia: ([\d.]+)/);
      const score = relevanceMatch ? parseFloat(relevanceMatch[1]) : undefined;

      if (url && title) {
          result.results?.push({
              title,
              url,
              content: snippet || body.replace(/\n/g, ' ').substring(0, 150) + '...', // Fallback snippet
              favicon: `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=32`, // Generic favicon service
              score: score
          });
      }
  }

  return result;
}

export default WebSearchResult;
